<script lang="ts">
  import { MaterialName, type StickerPreviewLayerProps } from '../types';
  import {
    centerSVGChildElement,
    centerSVGChildElementAndCounteractAnimationsTransform,
  } from '../svg-utils';

  let { selectedMaterial, settings = $bindable(), uniqueId }: StickerPreviewLayerProps = $props();

  let printLayer: SVGUseElement;
  let mixLayer: SVGUseElement | undefined = $state();
  let baseplateLayer: SVGUseElement;
  let holographicLayer: SVGUseElement | undefined = $state();
  let glitterLayer: SVGUseElement | undefined = $state();
  let prismaticLayer: SVGUseElement | undefined = $state();
  let metallicLayer: SVGUseElement | undefined = $state();
  let brushedLayer: SVGUseElement | undefined = $state();
  let laminateLayer: SVGUseElement | undefined = $state();
  let recycledLayer: SVGUseElement | undefined = $state();
  let printRef: SVGUseElement | undefined = $state();
  let clippyRef: SVGUseElement | undefined = $state();
  let laminateOpacity = $derived(selectedMaterial.laminateSettings.laminateOpacity ?? 0);

  $effect(() => {
    let svg = document.querySelector(`#canvas-${uniqueId}`) as SVGSVGElement;
    /*     console.log('effect in layers', sticker); */
    if (svg) {
      if (holographicLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, holographicLayer);
      }
      if (glitterLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, glitterLayer);
      }
      if (metallicLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, metallicLayer);
      }
      if (laminateLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, laminateLayer);
      }
      if (baseplateLayer) {
        centerSVGChildElementAndCounteractAnimationsTransform(svg, baseplateLayer);
      }
      let clippySvgPath = document.querySelector(`#clippySvgPath-${uniqueId}`);
      clippySvgPath?.removeAttribute('transform');
      if (clippySvgPath && clippyRef) {
        clippyRef?.removeAttribute('transform');
        let transformRef = centerSVGChildElement(svg, clippyRef);
        clippySvgPath.setAttribute('transform', transformRef);
      }
    }
  });

  $effect(() => {
    /* Handling masks */
    if (baseplateLayer) {
      if (selectedMaterial.type == MaterialName.Recycled) {
        baseplateLayer.style.fill = 'url(#recycled-pattern-' + uniqueId + ')';
      }
    }
    if (printLayer) {
      if (selectedMaterial.type == MaterialName.Clear) {
        printLayer.style.mask = 'url(#combined-mask-' + uniqueId + ')';
      }
    }
  });
  // Mix mode blend mode multiply (MMBMM) layer
  $effect(() => {
    if (mixLayer) {
      if (
        selectedMaterial.type === MaterialName.Glitter ||
        selectedMaterial.type === MaterialName.Pixie
      ) {
        mixLayer.style.mixBlendMode = 'color-burn';
      } else {
        mixLayer.style.mixBlendMode = 'multiply';
      }
    }
  });
</script>

<use opacity="0" bind:this={clippyRef} href="#clippySvgPath-{uniqueId}"></use>

<g class={selectedMaterial.type} clip-path="url(#clippy-{uniqueId})" style="z-index: -1;">
  <!--  -->
  <use
    bind:this={baseplateLayer}
    id="baseplateLayer-{uniqueId}"
    href="#baseplate-{uniqueId}"
    class:effectLayerDisabled={!settings.effectLayerEnabled}
    class="basePlateLayer"
  ></use>
  {#if selectedMaterial.type === MaterialName.Holographic}
    <use
      bind:this={holographicLayer}
      id="holographicLayer-{uniqueId}"
      href="#holographicLayerDef-{uniqueId}"
    />
  {:else if selectedMaterial.type === MaterialName.Glitter || selectedMaterial.type === MaterialName.Pixie}
    <g mask="url(#glitterMask-{uniqueId})">
      <use
        bind:this={glitterLayer}
        id="glitterLayer-{uniqueId}"
        href="#glitterLayerDef-{uniqueId}"
        class="glitterLayer"
      />
    </g>
  {:else if selectedMaterial.type === MaterialName.Prismatic}
    <use bind:this={prismaticLayer} href="#prismaticLayerDef-{uniqueId}" />
  {/if}

  <!-- Metallic Shine Image Layer. (Same as glossy laminate?) -->
  {#if selectedMaterial.isMetal}
    <use
      bind:this={metallicLayer}
      id="metallicLayer-{uniqueId}"
      href="#metallicLayerDef-{uniqueId}"
    />
  {/if}
  <!-- Brushed Aluminum  -->
  {#if selectedMaterial.type === MaterialName.Brushed}
    <use
      bind:this={brushedLayer}
      id="brushedLayer-{uniqueId}"
      href="#brushedLayerDef-{uniqueId}"
      opacity=".3"
    ></use>
  {/if}
  <!-- Reflective  -->
  {#if selectedMaterial.type === MaterialName.Reflective}
    <use id="reflectiveLayer-{uniqueId}" href="#reflectiveLayerDef-{uniqueId}"></use>
  {/if}
  <!-- Print Image Layer With printimage and bleed-->
  <use
    bind:this={printLayer}
    id="printLayer-{uniqueId}"
    href="#imageGroup-{uniqueId}"
    mask="url(#combined-mask-{uniqueId})"
    class="printLayer"
  ></use>
  <!-- PrintRef for getting bbox of image -->
  <use
    bind:this={printRef}
    id="printref-{uniqueId}"
    href="#printImage-{uniqueId}"
    style="opacity:0;"
  ></use>
  <!-- Mix Mode Blend Mode Multiply (MMBMM) Layer-->
  {#if settings.effectLayerEnabled}
    <use
      id="mixModeMaskLayer-{uniqueId}"
      bind:this={mixLayer}
      href="#imageGroup-{uniqueId}"
      mask="url(#mix-mask-{uniqueId})"
      opacity={selectedMaterial.type === MaterialName.Clear ? 0.5 : 0.8}
    />
  {/if}
  <!-- Laminate Image Layer TODO: Why is 0.01 opacity if its disabled? -->
  {#if settings.laminateLayerEnabled}
    {#if settings.lightweight}
      <use
        bind:this={laminateLayer}
        id="laminateLayer-{uniqueId}"
        href="#laminateLayerDefAnimation-{uniqueId}"
        opacity={selectedMaterial.laminateSettings.enabled ? laminateOpacity : 0.01}
      ></use>
    {:else}
      <use
        bind:this={laminateLayer}
        id="laminateLayer-{uniqueId}"
        href="#laminateLayerDef-{uniqueId}"
        filter="url(#displacementFilter-{uniqueId})"
        opacity={selectedMaterial.laminateSettings.enabled ? laminateOpacity : 0.01}
      ></use>
    {/if}
  {/if}

  <!-- Kraft Paper -->
  {#if selectedMaterial.type === MaterialName.Recycled}
    <use
      bind:this={recycledLayer}
      id="recycled-extra-{uniqueId}"
      opacity=".2"
      href="#recycledLayerDef-{uniqueId}"
      class="recycled-extra"
    />
  {/if}
</g>

<style>
  .basePlateLayer {
    fill: transparent;
  }
  .clear .basePlateLayer {
    fill: #ffffff38;
    stroke: #a3a3a385;
    stroke-width: 1px;
  }
  .brushed_alloy .basePlateLayer,
  .reflective .basePlateLayer {
    fill: #c0c0c0;
  }
  .mirror .basePlateLayer {
    fill: #fefefe;
  }
  .prismatic .basePlateLayer {
    fill: #ffffff;
  }
  .holographic .basePlateLayer {
    fill: #ffffff;
  }
  .prismatic .basePlateLayer {
    fill: #bababa;
  }
  .effectLayerDisabled.basePlateLayer {
    fill: #fff;
  }
  .glitter .basePlateLayer,
  .pixie_dust .basePlateLayer {
    fill: #aaaaaa;
  }
  .gitd .basePlateLayer,
  .gitd_industrial .basePlateLayer {
    fill: #fdffe0;
  }
  .kraft_paper .printLayer {
    opacity: 0.9;
  }
  .recycled-extra {
    pointer-events: none;
    filter: saturate(0) contrast(3);
    opacity: 0.2;
  }
  .glitterLayer {
    mix-blend-mode: color-dodge;
  }
</style>
